/**
 * PageLiveExamples — a second realtime block per page.
 *
 * While PageLivePulse shows aggregate counters + a network ticker,
 * this component walks visitors through concrete, realtime example
 * scenarios that play out live: an appointment being booked, a
 * prescription being signed, a WhatsApp reminder being sent, etc.
 *
 * Each scenario auto-advances through 4 steps with a progress bar,
 * then rotates to the next scenario. Fully self-contained; drop-in
 * on any public page and tint with the page accent.
 */
import { useEffect, useState } from "react";
import {
  CalendarCheck2,
  FileSignature,
  MessageCircle,
  Radio,
  Receipt,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";

type Step = { at: string; text: string };
type Scenario = {
  icon: LucideIcon;
  tag: string;
  title: string;
  steps: Step[];
};

const SCENARIOS: Scenario[] = [
  {
    icon: CalendarCheck2,
    tag: "Appointment",
    title: "A patient in Dhaka just booked a slot",
    steps: [
      { at: "10:42:11", text: "Rahim (34, M) opens Dr. Karim's public page" },
      { at: "10:42:19", text: "Picks Tomorrow · 11:30 AM at Green Chamber" },
      { at: "10:42:24", text: "Serial #14 issued · WhatsApp confirmation sent" },
      { at: "10:42:26", text: "Doctor's queue and daily sheet updated live" },
    ],
  },
  {
    icon: FileSignature,
    tag: "Prescription",
    title: "Dr. Rahman is signing an Rx right now",
    steps: [
      { at: "10:43:02", text: "Diagnosis · Type 2 Diabetes (E11.9) added" },
      { at: "10:43:10", text: "Metformin 500mg · 1+0+1 · 30 days autocompleted" },
      { at: "10:43:18", text: "AI flagged interaction with existing Amlodipine" },
      { at: "10:43:22", text: "Signed · PDF emailed · patient portal updated" },
    ],
  },
  {
    icon: MessageCircle,
    tag: "WhatsApp",
    title: "12 follow-up reminders going out via WhatsApp",
    steps: [
      { at: "10:44:00", text: "Cron sweeps follow-ups scheduled for today" },
      { at: "10:44:03", text: "Bilingual template rendered per patient" },
      { at: "10:44:07", text: "Twilio queue accepted · delivery in <5s" },
      { at: "10:44:12", text: "3 patients replied 'Confirm' · slots held" },
    ],
  },
  {
    icon: Receipt,
    tag: "Billing",
    title: "৳4,200 collected via bKash in Chittagong",
    steps: [
      { at: "10:44:41", text: "Invoice #INV-2891 marked Partial" },
      { at: "10:44:48", text: "bKash IPN received · txn verified" },
      { at: "10:44:50", text: "Daily income register (হিসাব খাতা) updated" },
      { at: "10:44:52", text: "Patient receipt PDF sent · balance ৳0" },
    ],
  },
];

const STEP_MS = 1400;

export default function PageLiveExamples({
  accent = "#1ba179",
  title = "See it happen in real time",
  subtitle = "Real scenarios doctors run on DoctorsX every minute — playing out live below.",
}: {
  accent?: string;
  title?: string;
  subtitle?: string;
}) {
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStepIdx((s) => {
        if (s + 1 >= SCENARIOS[scenarioIdx].steps.length) {
          setScenarioIdx((i) => (i + 1) % SCENARIOS.length);
          return 0;
        }
        return s + 1;
      });
    }, STEP_MS);
    return () => clearInterval(id);
  }, [scenarioIdx]);

  const active = SCENARIOS[scenarioIdx];
  const ActiveIcon = active.icon;
  const progress = ((stepIdx + 1) / active.steps.length) * 100;

  return (
    <section
      className="relative rounded-3xl border overflow-hidden mb-16 p-6 md:p-10 bg-white"
      style={{ borderColor: `${accent}22` }}
      aria-label="Live example scenarios"
    >
      <div
        className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-20"
        style={{ background: accent }}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 relative">
        <div>
          <div
            className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider mb-3 border"
            style={{ color: accent, borderColor: `${accent}40`, background: `${accent}0d` }}
          >
            <Radio className="w-3 h-3" />
            Realtime · Streaming now
          </div>
          <h3
            className="text-[22px] md:text-[26px] leading-tight font-extrabold tracking-tight text-[#0f172a]"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}
          >
            {title}
          </h3>
          <p className="text-[13.5px] text-[#0f172a]/60 mt-1 max-w-xl">{subtitle}</p>
        </div>

        {/* Scenario chips */}
        <div className="flex flex-wrap gap-1.5">
          {SCENARIOS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === scenarioIdx;
            return (
              <button
                key={s.tag}
                onClick={() => {
                  setScenarioIdx(i);
                  setStepIdx(0);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition"
                style={
                  isActive
                    ? { background: accent, color: "#fff", borderColor: accent }
                    : { color: "#0f172a", borderColor: "#e2e8f0", background: "#fff" }
                }
              >
                <Icon className="w-3 h-3" />
                {s.tag}
              </button>
            );
          })}
        </div>
      </div>

      {/* Live console */}
      <div
        className="relative rounded-2xl border overflow-hidden shadow-sm"
        style={{ borderColor: `${accent}20` }}
      >
        {/* Terminal chrome */}
        <div
          className="flex items-center justify-between px-4 py-2 border-b bg-[#f8fafc]"
          style={{ borderColor: `${accent}18` }}
        >
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#fb7185]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#fbbf24]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#34d399]" />
          </div>
          <div className="flex items-center gap-2 text-[10.5px] font-mono text-[#0f172a]/50">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: accent }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: accent }} />
            </span>
            live.doctorsx.com/stream
          </div>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: accent }}>
            <ActiveIcon className="w-3.5 h-3.5" />
            {active.tag}
          </div>
        </div>

        {/* Scenario title */}
        <div className="px-5 md:px-6 pt-5 pb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#0f172a]/50 mb-1">
            Now streaming
          </div>
          <div
            className="text-[16px] md:text-[18px] font-bold text-[#0f172a]"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}
          >
            {active.title}
          </div>
        </div>

        {/* Steps log */}
        <ol className="px-5 md:px-6 pb-5 space-y-2 font-mono text-[12.5px]">
          {active.steps.map((step, i) => {
            const visible = i <= stepIdx;
            const isCurrent = i === stepIdx;
            return (
              <li
                key={`${scenarioIdx}-${i}`}
                className="flex items-start gap-3 rounded-xl border px-3.5 py-2.5 transition-all duration-500"
                style={{
                  opacity: visible ? 1 : 0.25,
                  transform: visible ? "translateY(0)" : "translateY(4px)",
                  borderColor: isCurrent ? `${accent}55` : "#eef2f7",
                  background: isCurrent ? `${accent}08` : "#fff",
                }}
              >
                <span
                  className="mt-0.5 shrink-0 text-[10.5px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                  style={{ color: accent, background: `${accent}12` }}
                >
                  {step.at}
                </span>
                <span className="text-[#0f172a]/85 leading-relaxed">{step.text}</span>
                {isCurrent && (
                  <span
                    className="ml-auto shrink-0 relative flex h-2 w-2 mt-1.5"
                    aria-hidden
                  >
                    <span
                      className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70"
                      style={{ background: accent }}
                    />
                    <span
                      className="relative inline-flex rounded-full h-2 w-2"
                      style={{ background: accent }}
                    />
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        {/* Progress bar */}
        <div className="h-1 bg-[#f1f5f9]">
          <div
            className="h-full transition-[width] duration-[1400ms] ease-linear"
            style={{ width: `${progress}%`, background: accent }}
          />
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-4 flex items-center gap-2 text-[11.5px] text-[#0f172a]/55 relative">
        <Stethoscope className="w-3.5 h-3.5" style={{ color: accent }} />
        Realtime activity from anonymised network events. Click a chip to replay a scenario.
      </div>
    </section>
  );
}
